import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ZUSTAND } from '../lib/cmdb'
import type { CmdbAenderung, Customer, Device, Dienst } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { AenderungenTabelle } from '../components/CmdbKarten'

interface Props {
  customerSlug: string | null
}

/**
 * Dienste & CMDB (Stufe 5.4): die Dienste eines Kunden mit dem, woran sie
 * hängen, und die Geschichte der Stammdaten.
 */
export function DienstePage({ customerSlug }: Props) {
  const [ansicht, setAnsicht] = useState<'dienste' | 'aenderungen'>('dienste')
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    staleTime: 60_000,
    enabled: Boolean(customerSlug),
  })
  const customer = customers.data?.find((c) => c.slug === customerSlug)

  return (
    <>
      <div role="tablist" aria-label="Ansicht" className="reiterleiste">
        {(
          [
            ['dienste', 'Dienste'],
            ['aenderungen', 'Änderungen'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={ansicht === key}
            className="reiter"
            onClick={() => setAnsicht(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {customerSlug && !customer ? (
        <Loading what="Den Kunden" />
      ) : ansicht === 'dienste' ? (
        <DiensteAnsicht customer={customer} />
      ) : (
        <AenderungenAnsicht customer={customer} />
      )}
    </>
  )
}

function DiensteAnsicht({ customer }: { customer?: Customer }) {
  const { canForCustomer } = useAuth()
  const dienste = useQuery({
    queryKey: ['dienste', customer?.id ?? null],
    queryFn: ({ signal }) => apiGet<Dienst[]>(`/dienste${customer ? `?kunde=${customer.id}` : ''}`, signal),
    refetchInterval: 30_000,
  })
  const geraete = useQuery({
    queryKey: ['devices'],
    queryFn: ({ signal }) => apiGet<Device[]>('/devices', signal),
    staleTime: 60_000,
  })

  if (dienste.isPending) return <Loading what="Die Dienste" />
  if (dienste.isError) return <ErrorBox error={dienste.error} what="Die Dienste" />
  const liste = dienste.data
  const gestoert = liste.filter((d) => d.zustand === 'gestoert').length

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Dienste</h1>
          <div className="page-sub">
            {customer?.name ?? 'Alle Kunden'} · {liste.length} Dienste, {gestoert} gestört
          </div>
        </div>
      </div>
      {customer && canForCustomer(customer.id, 'device.manage') ? <DienstAnlegen customer={customer} /> : null}
      {liste.length === 0 ? (
        <EmptyState
          title="Noch keine Dienste"
          hint={
            customer
              ? 'Ein Dienst ist, was der Kunde nutzt – „Internet“, „VPN Zentrale“, „WLAN Gäste“. Er hängt an Geräten und an anderen Diensten.'
              : 'Dienste gelten je Kunde: oben links den Kunden wählen.'
          }
        />
      ) : (
        liste.map((d) => (
          <DienstKarte
            key={d.id}
            dienst={d}
            alleDienste={liste}
            geraete={(geraete.data ?? []).filter((g) => g.customer_id === d.kunde_id)}
            darf={canForCustomer(d.kunde_id, 'device.manage')}
            mitKunde={!customer}
          />
        ))
      )}
    </>
  )
}

function DienstAnlegen({ customer }: { customer: Customer }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [beschreibung, setBeschreibung] = useState('')
  const anlegen = useMutation({
    mutationFn: () =>
      apiSend<Dienst>('POST', '/dienste', {
        kunde_id: customer.id,
        name: name.trim(),
        beschreibung: beschreibung.trim(),
      }),
    onSuccess: () => {
      setName('')
      setBeschreibung('')
      void queryClient.invalidateQueries({ queryKey: ['dienste'] })
    },
  })
  return (
    <section className="card">
      <form
        className="card-body row"
        style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
        onSubmit={(e) => {
          e.preventDefault()
          anlegen.mutate()
        }}
      >
        <div className="field">
          <label htmlFor="dienst-name">Name des Dienstes</label>
          <input id="dienst-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field grow">
          <label htmlFor="dienst-beschreibung">Beschreibung</label>
          <input
            id="dienst-beschreibung"
            className="input"
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-sm" disabled={anlegen.isPending || !name.trim()}>
          Dienst hinzufügen
        </button>
        {anlegen.isError ? <span style={{ color: 'var(--crit)' }}>{(anlegen.error as Error).message}</span> : null}
      </form>
    </section>
  )
}

function DienstKarte({
  dienst,
  alleDienste,
  geraete,
  darf,
  mitKunde,
}: {
  dienst: Dienst
  alleDienste: Dienst[]
  geraete: Device[]
  darf: boolean
  mitKunde: boolean
}) {
  const queryClient = useQueryClient()
  const [ziel, setZiel] = useState('')
  const [gruppe, setGruppe] = useState('')
  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['dienste'] })
    void queryClient.invalidateQueries({ queryKey: ['device-dienste'] })
  }
  const hinzufuegen = useMutation({
    mutationFn: () => {
      const [art, id] = ziel.split(':')
      return apiSend('POST', `/dienste/${dienst.id}/abhaengigkeiten`, {
        ...(art === 'g' ? { geraet_id: id } : { dienst_id: id }),
        gruppe: gruppe.trim(),
      })
    },
    onSuccess: () => {
      setZiel('')
      setGruppe('')
      neuLaden()
    },
  })
  const entfernen = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/dienst-abhaengigkeiten/${id}`),
    onSuccess: neuLaden,
  })
  const loeschen = useMutation({
    mutationFn: () => apiSend<void>('DELETE', `/dienste/${dienst.id}`),
    onSuccess: neuLaden,
  })
  const vergeben = new Set(dienst.abhaengigkeiten.map((a) => a.geraet_id ?? a.dienst_id))
  const andere = alleDienste.filter((d) => d.kunde_id === dienst.kunde_id && d.id !== dienst.id && !vergeben.has(d.id))

  return (
    <section className="card" aria-label={dienst.name}>
      <div className="card-head">
        <div>
          <h2 className="card-title">{dienst.name}</h2>
          <div className="muted">
            {mitKunde ? `${dienst.kunde} · ` : ''}
            {dienst.beschreibung}
          </div>
        </div>
        <span className="row" style={{ gap: 8 }}>
          <span className={ZUSTAND[dienst.zustand].klasse}>{ZUSTAND[dienst.zustand].text}</span>
          {darf ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={loeschen.isPending}
              onClick={() => loeschen.mutate()}
            >
              Dienst löschen
            </button>
          ) : null}
        </span>
      </div>
      {dienst.ursachen.length > 0 ? (
        <div className="card-body" style={{ color: 'var(--crit)' }}>
          Ursache: {dienst.ursachen.join(', ')} nicht erreichbar
        </div>
      ) : null}
      {dienst.abhaengigkeiten.length === 0 ? (
        <div className="card-body muted">Hängt noch an nichts – der Zustand ist deshalb unbekannt.</div>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Hängt an</th>
              <th>Redundanz</th>
              <th>Zustand</th>
              {darf ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {dienst.abhaengigkeiten.map((a) => (
              <tr key={a.id}>
                <td>{a.geraet ?? `Dienst ${a.dienst ?? ''}`}</td>
                <td>{a.gruppe ? `Gruppe ${a.gruppe}` : <span className="muted">keine</span>}</td>
                <td>
                  <span className={ZUSTAND[a.zustand].klasse}>{ZUSTAND[a.zustand].text}</span>
                </td>
                {darf ? (
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={entfernen.isPending}
                      onClick={() => entfernen.mutate(a.id)}
                    >
                      Entfernen
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {darf ? (
        <form
          className="card-body row"
          style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--border)' }}
          onSubmit={(e) => {
            e.preventDefault()
            hinzufuegen.mutate()
          }}
        >
          <div className="field grow">
            <label htmlFor={`ziel-${dienst.id}`}>Hängt an</label>
            <select id={`ziel-${dienst.id}`} className="input" value={ziel} onChange={(e) => setZiel(e.target.value)}>
              <option value="">Gerät oder Dienst wählen</option>
              <optgroup label="Geräte">
                {geraete
                  .filter((g) => !vergeben.has(g.id))
                  .map((g) => (
                    <option key={g.id} value={`g:${g.id}`}>
                      {g.name}
                    </option>
                  ))}
              </optgroup>
              <optgroup label="Dienste">
                {andere.map((d) => (
                  <option key={d.id} value={`d:${d.id}`}>
                    {d.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className="field">
            <label htmlFor={`gruppe-${dienst.id}`}>Redundanz-Gruppe</label>
            <input
              id={`gruppe-${dienst.id}`}
              className="input"
              placeholder="z. B. WAN"
              value={gruppe}
              onChange={(e) => setGruppe(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-sm" disabled={hinzufuegen.isPending || !ziel}>
            Abhängigkeit hinzufügen
          </button>
          {hinzufuegen.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(hinzufuegen.error as Error).message}</span>
          ) : null}
        </form>
      ) : null}
    </section>
  )
}

function AenderungenAnsicht({ customer }: { customer?: Customer }) {
  const liste = useQuery({
    queryKey: ['cmdb', 'alle', customer?.id ?? null],
    queryFn: ({ signal }) =>
      apiGet<CmdbAenderung[]>(`/cmdb/aenderungen?anzahl=300${customer ? `&kunde=${customer.id}` : ''}`, signal),
    refetchInterval: 60_000,
  })
  if (liste.isPending) return <Loading what="Die Änderungen" />
  if (liste.isError) return <ErrorBox error={liste.error} what="Die Änderungen" />
  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Änderungen</h1>
          <div className="page-sub">
            {customer?.name ?? 'Alle Kunden'} · Stammdaten der Geräte und Dienste, neueste zuerst
          </div>
        </div>
      </div>
      {liste.data.length === 0 ? (
        <EmptyState title="Noch keine Änderungen" hint="Jede Änderung an Geräten und Diensten erscheint hier." />
      ) : (
        <section className="card">
          <AenderungenTabelle zeilen={liste.data} mitObjekt />
        </section>
      )}
    </>
  )
}
